import axios from 'axios';

import { RecaptchaService } from './recaptcha';
import { getIdentifier } from './fingerprint';

const toroOidcClient = axios.create({ baseURL: '' });
const ALLOWED_COOKIE_KEYS = [
    '_interaction',
    '_interaction.sig',
    'ak_bmsc',
    'bm_sv',
    '_session',
    '_session.legacy',
    '_session.legacy.sig',
    '_session.sig',
    'oauth$rememberToken'
]; // Chaves padrões para autenticar no HB Clássico

/**
 * Realiza login oauth
 * @param uid
 * @param login
 * @param password
 * @returns
 */
export const login = async (uid: string, login: string, password: string) => {
    const response = await toroOidcClient.post(
        `/oauth/interaction/${uid}/login`,
        new URLSearchParams({
            login,
            password
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...(await getBaseHeaders(login))
            }
        }
    );

    return { redirected: true, location: response.data.location };
};

/**
 * Realiza login oauth com MFA
 * @param uid
 * @param login
 * @param password
 * @param code
 * @param toroSession
 * @returns
 */
export const loginWithMFA = async (uid: string, login: string, password: string, code: string, toroSession: string) => {
    const response = await toroOidcClient.post(
        `/oauth/interaction/${uid}/mfa`,
        new URLSearchParams({
            login,
            password,
            code,
            toroSession
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...(await getBaseHeaders(login, toroSession))
            }
        }
    );

    return { redirected: true, location: response.data.location };
};

/**
 * Confirma permissões de dados do cliente
 * @param uid
 * @returns
 */
export const confirm = async (uid: string) => {
    const response = await toroOidcClient.post(`/oauth/interaction/${uid}/confirm`);
    return response.data;
};

/**
 * Envia MFA via SMS
 * @param login
 * @param toroSession
 * @returns
 */
export const sendSMS = async (login: string, toroSession: string) => {
    const response = await toroOidcClient.post(
        '/oauth/login/sendsms',
        { login, toroSession },
        {
            headers: await getBaseHeaders(login, toroSession)
        }
    );

    return response.data;
};

/**
 * Determina os cabeçalhos base comuns de toda requisição
 * @param login
 * @param uid
 * @param sessionId
 * @returns
 */
const getBaseHeaders = async (login: string, sessionId?: string) => {
    const [captcha, fingerprint] = await Promise.all([RecaptchaService.getToken('LOGIN'), getIdentifier()]);
    const currentCookies = typeof document !== 'undefined' ? document.cookie : '';
    const cleanCookie = filterCookies(currentCookies, ALLOWED_COOKIE_KEYS);

    return {
        'x-sessionId': sessionId,
        'x-toro-platform-identifier': fingerprint?.visitorId,
        ...captcha.asHeaders(),
        'x-captcha-metadata': btoa(
            JSON.stringify({
                username: login?.slice(0, Math.min(6, login.length)),
                applicationId: 'toro_auth'
            })
        ),
        ...(cleanCookie ? { Cookie: cleanCookie } : {})
    };
};

/**
 * Seleciona as Keys que estiverem no Cookie para que seja mantida.
 * @param cookieString A string bruta do header 'Cookie' (ex: "key1=value; key2=value")
 * @param allowedKeys Array de strings com as chaves que devem ser preservadas
 * @returns String de cookies filtrada e formatada
 */
const filterCookies = (cookieString: string | undefined, allowedKeys: string[]): string => {
    if (!cookieString) return '';

    return cookieString
        .split(';') // Separa os cookies individuais
        .map((cookie) => cookie.trim()) // Remove espaços extras
        .filter((cookie) => {
            // Pega a chave do cookie (tudo antes do primeiro '=')
            const key = cookie.split('=')[0];
            return allowedKeys.includes(key);
        })
        .join('; '); // Remonta a string no padrão HTTP
};
